import { errorMessage, isUnreachableError } from "@/lib/errors";
import { PB_DISPLAY_URL } from "@/lib/pb";
import { Button } from "./Button";
import { StatusPanel } from "./StatusPanel";

export type LoadFailureProps = {
  /** Whatever the query threw. */
  error: unknown;
  /**
   * The title for a real failure — a 500, a bad response, a broken query.
   * One voice across the app: "Could not load <the thing>".
   */
  title: string;
  /**
   * The reassurance sentence for the offline case, in the shape "<the data> is
   * safe there …". It follows "Nothing answered at <host>." and its job is to
   * say what is *not* lost, because that is the actual question the user has.
   */
  offlineNote: string;
  onRetry: () => void;
  retrying?: boolean;
  /**
   * Force the offline branch even though no error was thrown — for a query that
   * stalled instead of failing. See `lib/useOutageStall`.
   */
  offline?: boolean;
};

/**
 * "This screen has no data and here is why."
 *
 * The two cases used to be written out by hand on every surface, and they had
 * drifted: four different error titles, and only two of the four screens said
 * which server they had failed to reach. Both of those are the same class of
 * bug — the user cannot tell whether the problem is theirs, and the app sounds
 * like four different products depending on which tab they are on.
 *
 * So the distinction lives here instead:
 *
 * - **Unreachable** — nothing is wrong with the data, the device just cannot
 *   see it. Always names the host, always says the work is safe. This is the
 *   reassuring case and it must not read as an error.
 * - **Anything else** — a real failure. Named plainly, with the underlying
 *   message shown rather than swallowed, so it can be reported.
 */
export function LoadFailure({
  error,
  title,
  offlineNote,
  onRetry,
  retrying = false,
  offline: forcedOffline = false,
}: LoadFailureProps) {
  const offline = forcedOffline || isUnreachableError(error);

  return (
    <StatusPanel
      tone={offline ? "offline" : "error"}
      icon={offline ? <OfflineIcon /> : <AlertIcon />}
      title={offline ? "Cannot reach the Kobo server" : title}
      description={
        offline ? (
          <>
            Nothing answered at <span className="numeric">{PB_DISPLAY_URL}</span>. {offlineNote}
          </>
        ) : (
          errorMessage(error)
        )
      }
      actions={
        <Button variant="primary" onClick={onRetry} loading={retrying}>
          Try again
        </Button>
      }
    />
  );
}

function OfflineIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden className="size-7 stroke-current" fill="none">
      <path
        d="M3.5 3.5l17 17M5.2 9.4a11 11 0 013.2-1.9M18.9 9.5A11 11 0 0016 7.6M8.4 13.1a6.5 6.5 0 012-1M15.7 13.2a6.5 6.5 0 00-1.6-.9M12 17.6h.01"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
    </svg>
  );
}

function AlertIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden className="size-7 stroke-current" fill="none">
      <circle cx="12" cy="12" r="8.6" strokeWidth="1.7" />
      <path d="M12 7.8v5M12 16.1h.01" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}
